import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ExploreLinks from "./ExploreLinks";
import { openConsentSettings } from "./ConsentBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Footer — compact monochrome layout: brand + cryptocard promo on the left,
 * tight letterspaced link columns on the right, SEO explore strip above,
 * hairline bottom bar with copyright + legal links.
 * Operator attribution (crypt.pe · Costa Rica) is mandatory on every page.
 */
const OPERATOR = "CRYPT.PE";

function ColumnHead({ children }) {
  return (
    <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400 mb-4">
      {children}
    </div>
  );
}

// Aggregate stars next to the footer reviews link — only once there's a
// meaningful number of real merchant reviews (>= 5), never fabricated.
function ReviewStars() {
  const [s, setS] = useState(null);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/reviews/public`)
      .then((r) => r.json())
      .then((d) => setS(d?.summary))
      .catch(() => {});
  }, []);
  if (!s || s.count < 5) return null;
  return (
    <span data-testid="footer-review-stars" className="ml-1.5 text-neutral-400">
      ★ {s.avg} ({s.count})
    </span>
  );
}

function FLink({ to, href, testId, children, follow = false }) {
  const className =
    "block text-[13px] text-neutral-400 hover:text-white transition-colors leading-none";
  if (href) {
    return (
      <a href={href} data-testid={testId} className={className} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? (follow ? "noopener noreferrer" : "nofollow noopener noreferrer") : undefined}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} className={className}>
      {children}
    </Link>
  );
}

function UptimeBadge() {
  const [pct, setPct] = useState(null);
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/status`)
      .then((r) => r.json())
      .then((d) => {
        const v = d?.uptime?.uptime_pct;
        if (typeof v === "number" && v >= 99) setPct(v);
      })
      .catch(() => {});
  }, []);
  return (
    <Link
      to="/status"
      data-testid="footer-uptime-badge"
      className="inline-flex items-center gap-1.5 sm:gap-2 border border-white/10 rounded-full px-2.5 sm:px-3 h-7 font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.08em] sm:tracking-[0.15em] text-neutral-500 hover:text-white hover:border-white/30 transition-colors w-fit whitespace-nowrap shrink-0"
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-50" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
      </span>
      {pct != null ? `${pct}% uptime · 30d` : "all systems operational"}
    </Link>
  );
}

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-black border-t border-white/10 overflow-hidden"
    >
      <div className="relative max-w-[1500px] mx-auto px-6 md:px-12 pt-8 md:pt-10">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_1fr] gap-12 lg:gap-16">
          {/* ─── Left brand column ─────────────────────────────────── */}
          <div className="flex flex-col gap-5">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Link to="/" data-testid="footer-logo-link" aria-label="crypt.pe — home" className="inline-block w-fit">
                <div
                  className="font-black uppercase tracking-[0.12em] text-[24px] text-white"
                  style={{ fontFamily: "'Satoshi', 'Inter', sans-serif" }}
                >
                  CRYPT.PE
                </div>
              </Link>
              <UptimeBadge />
            </div>

            {/* Cryptocard promo — compact */}
            <div className="relative w-full">
              <span className="absolute -top-3 left-5 z-10 bg-white text-black font-mono text-[9px] font-bold uppercase tracking-[0.3em] px-3 py-1.5">
                new launch
              </span>
              <Link to="/card" data-testid="footer-card-banner" className="group block relative overflow-hidden rounded-xl bg-black border border-white/10">
                <img
                  src="/cryptocard.webp"
                  alt="cryptocard"
                  loading="lazy"
                  className="w-full h-[118px] object-cover object-center opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-700"
                />
                <div aria-hidden className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-black/80 via-black/30 to-transparent" />
                <div className="absolute left-6 top-1/2 -translate-y-1/2 text-left">
                  <div className="font-mono text-[12px] font-bold uppercase tracking-[0.35em] text-white">
                    cryptocard
                  </div>
                  <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.3em] text-neutral-400 group-hover:text-white transition-colors">
                    join the waitlist →
                  </div>
                </div>
              </Link>
            </div>

            {/* Social — horizontal under the card */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="footer-social-row">
              <FLink href="https://x.com/crypt_pe" testId="footer-social-x">x / twitter</FLink>
              <FLink href="https://www.instagram.com/crypt.pe" testId="footer-social-instagram">instagram</FLink>
              <FLink href="https://www.threads.com/@crypt.pe" testId="footer-social-threads">threads</FLink>
              <FLink href="https://www.facebook.com/crypt.pe" testId="footer-social-facebook">facebook</FLink>
            </div>
          </div>

          {/* ─── Right: link columns ────────────────────────────────── */}
          <nav className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10">
            <div className="flex flex-col">
              <ColumnHead>product</ColumnHead>
              <div className="flex flex-col gap-3 grow justify-between">
                <FLink to="/crypto-invoicing" testId="footer-invoicing-link">crypto invoicing</FLink>
                <FLink to="/sell-products-for-crypto" testId="footer-products-link">crypto storefront</FLink>
                <FLink to="/crypto-payment-gateway" testId="footer-gateway-link">crypto payment gateway</FLink>
                <FLink to="/stablecoin-payment-gateway" testId="footer-stablecoin-link">stablecoin gateway</FLink>
                <FLink to="/card" testId="footer-card-link">crypto debit card</FLink>
                <FLink to="/products" testId="footer-all-products-link">all crypto <span className="whitespace-nowrap">products →</span></FLink>
              </div>
            </div>

            <div className="flex flex-col">
              <ColumnHead>resources</ColumnHead>
              <div className="flex flex-col gap-3 grow justify-between">
                <FLink to="/guide" testId="footer-guide-link">how it works</FLink>
                <FLink to="/guides" testId="footer-guides-link">merchant guides</FLink>
                <FLink to="/fees" testId="footer-fees-link">fees explained</FLink>
                <FLink to="/payment-help" testId="footer-payment-help-link">payment help</FLink>
                <FLink to="/savings-calculator" testId="footer-savings-link">savings calculator</FLink>
                <FLink to="/hackastra" testId="footer-demo-link">live demo</FLink>
                <FLink to="/faq" testId="footer-faq-link">faq</FLink>
              </div>
            </div>

            <div className="flex flex-col">
              <ColumnHead>company</ColumnHead>
              <div className="flex flex-col gap-3 grow justify-between">
                <FLink to="/about" testId="footer-about-link">about</FLink>
                <FLink to="/pricing" testId="footer-pricing-link">pricing</FLink>
                <FLink to="/security" testId="footer-security-link">security</FLink>
                <FLink to="/reviews" testId="footer-reviews-link">reviews<ReviewStars /></FLink>
                <FLink to="/case-studies" testId="footer-case-studies-link">case studies</FLink>
                <FLink to="/brand" testId="footer-brand-link">brand kit</FLink>
                <FLink href="mailto:legal@crypt.pe" testId="footer-contact-link">contact</FLink>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="flex flex-col grow">
                <ColumnHead>developers</ColumnHead>
                <div className="flex flex-col gap-3 grow justify-between">
                  <FLink to="/docs" testId="footer-docs-link">api docs</FLink>
                  <FLink to="/integrations" testId="footer-integrations-link">integrations</FLink>
                  <FLink href="https://github.com/shindeprakash-pixel/cryptpe-sdk" testId="footer-github-link" follow>github</FLink>
                  <FLink to="/status" testId="footer-status-link">status</FLink>
                  <FLink to="/changelog" testId="footer-changelog-link">changelog</FLink>
                </div>
              </div>
            </div>
          </nav>
        </div>

        <div className="mt-12 pt-10 border-t border-white/10">
          <ExploreLinks />
        </div>

        {/* ─── Bottom bar ──────────────────────────────────────────── */}
        <div className="mt-12 py-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div className="flex flex-col items-start gap-2 md:flex-row md:items-center md:gap-3 min-w-0 md:shrink-0 w-full md:w-auto">
            <div data-testid="footer-operator" className="w-full md:w-auto flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] lg:text-[12px] uppercase tracking-normal lg:tracking-wide text-neutral-400 leading-relaxed md:whitespace-nowrap">
              <span>© {year} <span className="text-neutral-400">{OPERATOR}</span></span>
              <span className="text-neutral-800">·</span>
              <span>3-102-969985 S.R.L.</span>
              <span className="text-neutral-800 hidden min-[360px]:inline">·</span>
              <span>SAN JOSÉ, COSTA RICA</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-0 md:justify-end w-full md:w-auto md:min-w-0 md:gap-x-3 lg:gap-x-4 text-[11px] lg:text-[12px] text-neutral-400 md:whitespace-nowrap [&>a]:inline-flex [&>a]:items-center [&>a]:min-h-[36px] md:[&>a]:min-h-0 [&>button]:inline-flex [&>button]:items-center [&>button]:min-h-[36px] md:[&>button]:min-h-0">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">
              <span className="lg:hidden">privacy</span><span className="hidden lg:inline">privacy policy</span>
            </Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white transition-colors">
              <span className="lg:hidden">T&amp;C</span><span className="hidden lg:inline">terms and conditions</span>
            </Link>
            <Link to="/compliance" data-testid="footer-compliance-link" className="hover:text-white transition-colors">
              compliance
            </Link>
            <button
              type="button"
              data-testid="footer-cookie-settings"
              onClick={() => openConsentSettings()}
              className="hover:text-white transition-colors"
            >
              cookie settings
            </button>
            <Link to="/disclaimer" data-testid="footer-disclaimer-link" className="hover:text-white transition-colors">disclosure</Link>
            <Link to="/risk" data-testid="footer-risk-link" className="hover:text-white transition-colors">risk</Link>
            <Link to="/aml" data-testid="footer-aml-link" className="hover:text-white transition-colors">aml</Link>
            <Link to="/cookies" data-testid="footer-cookies-link" className="hover:text-white transition-colors">cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
